import EPMDService from "@/services/epmd-service";
import IEpmdHeader from "@/models/epmd-header";
import { IDBPDatabase } from "idb/build/esm/entry";
import DataService from "@/services/data-service/data-service";
import AveragesService from "@/services/averages-service";
import IWasteTypeAverage from "@/models/waste-type-average";
import PoolDrainOptionsService from "@/services/pool-drain-options-service";
import IPoolDrainOptions from "@/models/pool-drain-options";
import IEpmdData from "@/models/epmd-data";
import { Vue } from "vue-property-decorator";
import VmsDbSchema from "@/models/VmsDbSchema";
import ISearchRequest from "@/models/SearchRequest";
import { isBefore, startOfDay, startOfMonth, subMonths } from "date-fns";
import WasteBulkRecordsService from "@/services/waste-bulk-records-service";
import IWasteBulkRecord from "@/models/waste-bulk-record";
import IEpmdRecord from "@/models/epmd-record";
import { EpmdTypeEnum } from "@/models/EpmdTypeEnum";
import {
  ActionIdEnum,
  FunctionToRunEnum,
  IDeleteProperties,
  IEPMDAddOrUpdateProperties,
  ModulesEnum
} from "@/services/data-service/action-queue-service";
import { BigIntString } from "@/services/BigIntString";

export default class DataEPMDService {
  private epmdService = new EPMDService();
  private averagesService = new AveragesService();
  private poolDrainOptionsService = new PoolDrainOptionsService();
  private wasteBulkRecordService = new WasteBulkRecordsService();
  private state = Vue.observable({
    epmdData: [] as IEpmdData[]
  });
  private readonly CACHE_MONTHS_EPMD_DATA = 0;
  private epmdWasteCombinations = [
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
    [2, 0],
    [2, 2],
    [3, 0]
  ];
  private strInt = new BigIntString();
  constructor(private $db: Promise<IDBPDatabase<VmsDbSchema>>, private $dataService: DataService) {}

  public actions = {
    GetHeader: this.GetHeader.bind(this),
    Lock: this.Lock.bind(this),
    UnLock: this.unLock.bind(this),
    GetData: this.epmdService.GetData,
    GetExcelData: this.epmdService.GetExcelData,
    GetPdfData: this.epmdService.GetPdfData,
    GetWasteTypesAverages: this.GetAverages.bind(this),
    GetPoolDrainOptions: this.GetPoolDrainOptions.bind(this),
    GetMinMaxDates: this.getMinMaxDateEpmdData.bind(this),
    AddOrUpdateRecords: this.addOrUpdateRecords.bind(this),
    AddOrUpdateRecordsQueue: this.addOrUpdateRecordsQueue.bind(this),
    FindWasteBulkRecord: this.findWasteBulkRecord.bind(this),
    TransformToEpmdData: this.transformToEpmdData.bind(this),
    DeleteWasteBulkRecord: this.deleteWasteBulkRecord.bind(this),
    DeleteWasteBulkRecordQueue: this.deleteWasteBulkRecordQueue.bind(this),
    searchEpmdData: this.searchEpmdData.bind(this)
  };

  public getters = {
    epmdData: () => this.state.epmdData
  };

  // ***** Initializations *****

  public initialize(shipIds: number[]) {
    return Promise.all([
      this.actions.GetWasteTypesAverages(),
      this.actions.GetPoolDrainOptions(),
      Promise.all(this.initializeHeaders()),
      Promise.all(this.initializeData(shipIds))
    ]);
  }
  private initializeHeaders() {
    return this.epmdWasteCombinations.map(([epmd, waste]) => this.actions.GetHeader(epmd, waste));
  }
  private initializeData(shipIds: number[]) {
    const requests = [];
    const startFrom = subMonths(startOfMonth(new Date()), this.CACHE_MONTHS_EPMD_DATA);

    for (const shipId of shipIds) {
      for (const epmd of [0, 2, 3]) {
        requests.push(
          (() =>
            this.searchEpmdData(epmd, 0, {
              dateFrom: startFrom,
              dateTo: new Date(),
              shipId: shipId,
              dateGroupBy: 0,
              portsId: []
            }))()
        );
      }
    }

    // for (const shipId of shipIds) {
    //   for (const group of [0, 1, 2]) {
    //     for (const [epmd, waste] of this.epmdWasteCombinations) {
    //       console.log(shipId, epmd, group, waste);
    //       requests.push((() =>
    //         this.searchEpmdData(epmd, waste, {
    //           dateFrom: startFrom,
    //           dateTo: new Date(),
    //           shipId: shipId,
    //           dateGroupBy: group,
    //           portsId: []
    //         }))()
    //       );
    //     }
    //   }
    // }
    return requests;
  }

  // **** Locking / Unlocking ****

  private async Lock(id: string) {
    const response = await this.epmdService.Lock(id);
    if (response.status !== 200) return;
    return this.modifyLockState(id, true, false);
  }

  private async unLock(id: string) {
    const response = await this.epmdService.UnLock(id);
    if (response.status !== 200) return;
    return this.modifyLockState(id, false, true);
  }

  private async modifyLockState(id: string, locked: boolean, overrideLock: boolean) {
    const index = this.state.epmdData.findIndex(x => x.id === id);
    if (index == undefined) return;
    Vue.set(
      this.state.epmdData,
      index,
      Object.assign(this.state.epmdData[index], {
        locked: locked,
        overrideLock: overrideLock
      })
    );
    const encrypted = await (await this.$db).get("EpmdData", id);
    if (encrypted == undefined) return;
    const data = DataService.decryptData<IEpmdData>(encrypted.value);
    const updatedData = DataService.encryptData(
      Object.assign(data, {
        locked: locked,
        overrideLock: overrideLock
      })
    );
    return (await this.$db).put(
      "EpmdData",
      Object.assign(encrypted, {
        value: updatedData
      })
    );
  }

  // **** Delete ****
  private async deleteWasteBulkRecordQueue(id: string, epmdType: EpmdTypeEnum): Promise<number> {
    if (!this.$dataService.getters.isOfflineSupport() || this.$dataService.getters.isOnline()) {
      await this.deleteWasteBulkRecord(id);
      return -1;
    }
    if (this.checkDuplicatePush(id).result === "forbidden") {
      throw Error("You cannot delete a record already in the queue!");
    }

    return this.$dataService.actions.FuncQueue.push({
      actionId: ActionIdEnum.DELETE,
      epmdType: epmdType,
      functionToRun: FunctionToRunEnum.EPMDDelete,
      moduleId: ModulesEnum.EPMD,
      msg: "Delete EPMD",
      properties: { id, type: "delete" }
    });
  }

  private async deleteWasteBulkRecord(id: string) {
    const data = (await this.wasteBulkRecordService.DeleteWasteBulkRecord(id)).data;
    const index = this.state.epmdData.findIndex(x => x.id === id);
    if (index == undefined) return;
    this.state.epmdData.splice(index, 1);
    await (await this.$db).delete("EpmdData", id);
    this.$dataService.actions.showMessage("success", "Record Deleted Successfully");
  }

  // **** Add/Edit/Delete Records *****
  private instanceOfIEPMDDeleteProperties(object: any): object is IDeleteProperties {
    return object?.type === "delete";
  }
  private instanceOfIEPMDAddOrUpdateProperties(object: any): object is IEPMDAddOrUpdateProperties {
    return object?.type === "addOrUpdate";
  }

  private checkDuplicatePush(id: string) {
    let result = {
      result: "allow",
      reason: ""
    };
    for (const q of this.$dataService.getters.FuncQueue.funcToRun()) {
      // if (
      //   !this.instanceOfIEPMDAddOrUpdateProperties(q.properties) &&
      //   !this.instanceOfIEPMDDeleteProperties(q.properties)
      // ) {
      //   continue;
      // }
      if (q.properties?.type === "delete") {
        if (q.properties.id === id) {
          result = {
            result: "forbidden",
            reason: "delete"
          };
          break;
        }
      } else if (q.properties?.type === "addOrUpdate") {
        if (q.properties.wasteBulkRecord.id && q.properties.wasteBulkRecord.id === id) {
          result = {
            result: "forbidden",
            reason: "edit"
          };
          break;
        }
      }
    }
    return result;
  }

  private async addOrUpdateRecordsQueue(
    epmdType: EpmdTypeEnum,
    wasteBulkRecord: IWasteBulkRecord,
    labels: IEpmdHeader[],
    action: ActionIdEnum.INSERT | ActionIdEnum.UPDATE
  ): Promise<number> {
    if (!this.$dataService.getters.isOfflineSupport() || this.$dataService.getters.isOnline()) {
      await this.addOrUpdateRecords(epmdType, wasteBulkRecord, labels);
      return -1;
    }
    if (
      action === ActionIdEnum.UPDATE &&
      wasteBulkRecord.id &&
      this.checkDuplicatePush(wasteBulkRecord.id).result === "forbidden"
    ) {
      throw Error("You cannot update a record already in the queue!");
    }
    return this.$dataService.actions.FuncQueue.push({
      actionId: action,
      epmdType: epmdType,
      functionToRun: FunctionToRunEnum.EPMDAddOrUpdate,
      moduleId: ModulesEnum.EPMD,
      msg: "Update/Admin EPMD",
      properties: {
        wasteBulkRecord: wasteBulkRecord,
        labels: labels,
        type: "addOrUpdate"
      }
    });
  }

  private async addOrUpdateRecords(
    epmdType: EpmdTypeEnum,
    wasteBulkRecord: IWasteBulkRecord,
    labels: IEpmdHeader[]
  ) {
    const updatedWasteRecord = (
      await this.wasteBulkRecordService.PostWasteBulkRecord(wasteBulkRecord)
    ).data;
    this.$dataService.actions.showMessage("success", "The record is created successfully");
    // Update local
    const transformedData = this.transformToEpmdData(updatedWasteRecord, labels);
    if (!transformedData) return;
    await this.setOneEPMDData(epmdType, transformedData);
    const index = this.state.epmdData.findIndex(x => x.id === transformedData.id);
    if (index >= 0) Vue.set(this.state.epmdData, index, Object.assign({}, transformedData));
    else this.state.epmdData.push(transformedData);
  }

  private async findWasteBulkRecord(
    epmdType: EpmdTypeEnum,
    date: Date,
    shipId: number,
    labels: IEpmdHeader[]
  ) {
    if (!this.$dataService.getters.isOfflineSupport())
      return this.transformToEpmdData(
        (await this.wasteBulkRecordService.FindWasteBulkRecord(epmdType, date, shipId))?.data,
        labels
      );
    const data = await this.getOneEpmdData(epmdType, date, shipId);
    return data
      ? data
      : this.transformToEpmdData(
          (await this.wasteBulkRecordService.FindWasteBulkRecord(epmdType, date, shipId))?.data,
          labels
        );
  }

  private transformToEpmdData(
    wasteRecord: IWasteBulkRecord | null,
    labels: IEpmdHeader[]
  ): IEpmdData | null {
    if (!wasteRecord) return null;
    const record: IEpmdRecord = {};
    if (wasteRecord.wasteBulks) {
      for (const w of wasteRecord.wasteBulks) {
        const currentLabel = labels.find(l => l.wasteTypeId === w.wasteTypeId);

        if (!currentLabel) continue;
        record[currentLabel.id] = {
          id: w.id,
          value: w.value
        };
      }
    }
    return {
      id: wasteRecord.id,
      date: wasteRecord.date,
      comment: wasteRecord.comment,
      shipId: wasteRecord.shipId,
      portId: wasteRecord.portId,
      investigation: wasteRecord.investigation,
      shipEvent: wasteRecord.shipEvent,
      overrideLock: wasteRecord.overrideLock,
      locked: wasteRecord.locked,
      rowVersionStr: wasteRecord.rowVersionStr,
      record
    } as IEpmdData;
  }

  // ***** EPMD Data  *****
  private async setOneEPMDData(epmdType: EpmdTypeEnum, data: IEpmdData) {
    if (data.id == undefined) throw Error("id must be defined in the data");
    return (await this.$db).put("EpmdData", {
      id: data.id,
      date: new Date(data.date),
      dateGroupBy: 0,
      wasteType: 0,
      epmdType,
      value: DataService.encryptData(data),
      shipId: data.shipId,
      rowVersion: data.rowVersionStr
    });
  }

  private async setEPMDData(
    epmdType: EpmdTypeEnum,
    wasteLevel: number,
    search: ISearchRequest,
    enableRowVersionData: boolean
  ) {
    if (search.shipId == undefined) throw Error("Ship is not defined!");
    if (search.dateFrom == undefined || search.dateTo == undefined)
      throw Error("Date Ranges not defined!");
    search.rowVersionDeleted = (
      await this.getMaxRowVersion(search.shipId, epmdType, wasteLevel, search.dateGroupBy ?? 0)
    )?.maxRowVersion;
    if (enableRowVersionData) search.rowVersionData = search.rowVersionDeleted;
    const { data, deleteds } = await this.epmdService.GetSyncData(epmdType, wasteLevel, search);
    const maxRowVersion =
      data && data.length > 0
        ? data.reduce((previousValue, currentValue) =>
            this.strInt.gt(previousValue?.rowVersionStr ?? 0, currentValue?.rowVersionStr ?? 0)
              ? previousValue
              : currentValue
          ).rowVersionStr
        : "0";
    await this.setMinMaxDateRowVersionEpmdData(
      search.dateFrom,
      search.dateTo,
      search.shipId,
      epmdType,
      wasteLevel,
      search.dateGroupBy ?? 0,
      maxRowVersion
    );
    try {
      const tx = (await this.$db).transaction("EpmdData", "readwrite");
      for (const d of data) {
        if (d.id == undefined) continue;
        tx.store.put({
          id: d.id,
          date: new Date(d.date),
          dateGroupBy: search.dateGroupBy ?? 0,
          epmdType: epmdType,
          wasteType: wasteLevel,
          value: DataService.encryptData(d),
          shipId: search.shipId,
          rowVersion: d.rowVersionStr
        });
      }
      for (const del of deleteds) {
        tx.store.delete(del.wasteBulkRecordId);
      }
      await tx.done;
    } catch (e: any) {
      console.error(e);
    }
    return data;
  }

  private async getOneEpmdData(epmdType: EpmdTypeEnum, date: Date, shipId: number) {
    const data = await (
      await this.$db
    ).getFromIndex("EpmdData", "shipId,epmdType,wasteType,dateGroupBy,date", [
      shipId,
      epmdType,
      0,
      0,
      date
    ]);
    return !data ? null : DataService.decryptData<IEpmdData>(data.value);
  }

  private async getEPMDData(epmdType: EpmdTypeEnum, wasteLevel: number, search: ISearchRequest) {
    const data: IEpmdData[] = [];
    const k = IDBKeyRange.bound(
      [
        search.shipId,
        epmdType,
        wasteLevel,
        search.dateGroupBy ?? 0,
        new Date(search.dateFrom as any)
      ],
      [search.shipId, epmdType, wasteLevel, search.dateGroupBy ?? 0, new Date(search.dateTo as any)]
    );
    let cursor = await (await this.$db)
      .transaction("EpmdData", "readonly")
      .store.index("shipId,epmdType,wasteType,dateGroupBy,date")
      .openCursor(k);
    while (cursor) {
      data.push(DataService.decryptData<IEpmdData>(cursor.value.value));
      cursor = await cursor.continue();
    }
    return data;
  }

  private async setMinMaxDateRowVersionEpmdData(
    minDate: Date,
    maxDate: Date,
    shipId: number,
    epmdType: EpmdTypeEnum,
    wasteType: number,
    dateGroupBy: number,
    maxRowVersion: string
  ) {
    const myDb = await this.$db;
    const key = await myDb.getKeyFromIndex(
      "EpmdMetaData",
      "shipId,epmdType,wasteType,dateGroupBy",
      [shipId, epmdType, wasteType, dateGroupBy]
    );
    if (key != undefined) await myDb.delete("EpmdMetaData", key);
    return myDb.put("EpmdMetaData", {
      minDate,
      maxDate,
      shipId,
      epmdType,
      wasteType,
      dateGroupBy,
      maxRowVersion
    });
  }

  public async getMinMaxDateEpmdData(
    shipId: number,
    epmdType: EpmdTypeEnum,
    wasteType: number,
    dateGroupBy: number
  ) {
    const data = await (
      await this.$db
    ).getFromIndex("EpmdMetaData", "shipId,epmdType,wasteType,dateGroupBy", [
      shipId,
      epmdType,
      wasteType,
      dateGroupBy
    ]);
    if (!data) return null;
    return { minDate: data.minDate, maxDate: data.maxDate };
  }

  private async getMaxRowVersion(
    shipId: number,
    epmdType: EpmdTypeEnum,
    wasteType: number,
    dateGroupBy: number
  ) {
    const data = await (
      await this.$db
    ).getFromIndex("EpmdMetaData", "shipId,epmdType,wasteType,dateGroupBy", [
      shipId,
      epmdType,
      wasteType,
      dateGroupBy
    ]);
    if (!data) return null;
    return data;
  }

  private async setMaxRowVersion(
    shipId: number,
    epmdType: EpmdTypeEnum,
    wasteType: number,
    dateGroupBy: number,
    newRowVersion: string
  ) {
    const currentMax = await this.getMaxRowVersion(shipId, epmdType, wasteType, dateGroupBy);
    if (!currentMax) return;
    if (this.strInt.gt(newRowVersion, currentMax.maxRowVersion)) {
      await (
        await this.$db
      ).put(
        "EpmdMetaData",
        Object.assign(currentMax, {
          maxRowVersion: newRowVersion
        }),
        currentMax.id
      );
    }
  }

  private async searchEpmdData(epmdType: EpmdTypeEnum, wasteLevel: number, search: ISearchRequest) {
    if (search.shipId == undefined) throw Error("Ship not defined!");
    if (!search.dateFrom || !search.dateTo) throw Error("From or To Dates are not defined");
    if (!this.$dataService.getters.isOfflineSupport()) {
      this.state.epmdData = await this.epmdService.GetData(epmdType, wasteLevel, search);
      return;
    }

    const dates = await this.getMinMaxDateEpmdData(
      search.shipId,
      epmdType,
      wasteLevel,
      search.dateGroupBy ?? 0
    );
    if (dates) {
      this.state.epmdData =
        isBefore(startOfDay(new Date(search.dateFrom)), startOfDay(new Date(dates.minDate))) &&
        this.$dataService.getters.isOnline()
          ? await this.setEPMDData(epmdType, wasteLevel, search, false)
          : await this.getEPMDData(epmdType, wasteLevel, search);
      return;
    }
    this.state.epmdData = await this.setEPMDData(epmdType, wasteLevel, search, true);
  }

  // ***** Averages *****
  private async GetAverages(shipId?: number) {
    if (!this.$dataService.getters.isOfflineSupport())
      return (await this.averagesService.GetWasteTypesAverages(shipId))?.data;
    return (await this.getEPMDAverage(shipId)) ?? (await this.setEPMDAverage(shipId));
  }

  private async getEPMDAverage(shipId?: number) {
    const encryptedData = await (await this.$db).get("EpmdAverages", shipId ?? -1);
    if (!encryptedData) return null;
    return DataService.decryptData<IWasteTypeAverage[]>(encryptedData);
  }

  private async setEPMDAverage(shipId?: number) {
    const data = (await this.averagesService.GetWasteTypesAverages(shipId))?.data ?? null;
    if (!data) throw Error("Cannot get the current EPMD Averages");
    const encryptData = DataService.encryptData(data);
    if (shipId == undefined) {
      await (await this.$db).put("EpmdAverages", encryptData, -1);
      for (const d of data) {
        const encryptD = DataService.encryptData(d);
        await (await this.$db).put("EpmdAverages", encryptD, d.shipId);
      }
    } else {
      await (await this.$db).put("EpmdAverages", encryptData, shipId);
    }

    return data;
  }

  // ***** Headers *****

  private async GetHeader(epmdType: EpmdTypeEnum, wasteLevel: number): Promise<IEpmdHeader[]> {
    if (!this.$dataService.getters.isOfflineSupport())
      return this.epmdService.GetHeader(epmdType, wasteLevel);
    return (
      (await this.getEPMDHeader(epmdType, wasteLevel)) ??
      (await this.setEPMDHeader(epmdType, wasteLevel))
    );
  }

  private static getEPMDHeaderHash(epmdType: EpmdTypeEnum, wasteLevel: number) {
    return epmdType * 100 + wasteLevel;
  }

  private async getEPMDHeader(epmdType: EpmdTypeEnum, wasteLevel: number) {
    const key = DataEPMDService.getEPMDHeaderHash(epmdType, wasteLevel);
    const encryptedData = await (await this.$db).get("EpmdHeaders", key);
    if (!encryptedData) return null;
    return DataService.decryptData<IEpmdHeader[]>(encryptedData);
  }

  private async setEPMDHeader(epmdType: EpmdTypeEnum, wasteLevel: number) {
    const key = DataEPMDService.getEPMDHeaderHash(epmdType, wasteLevel);
    const data = await this.epmdService.GetHeader(epmdType, wasteLevel);
    if (!data) throw Error("Cannot get the current EPMD headers");
    const encryptData = DataService.encryptData(data);
    await (await this.$db).put("EpmdHeaders", encryptData, key);
    return data;
  }

  // ***** Pool Drain Options *****

  private async GetPoolDrainOptions() {
    if (!this.$dataService.getters.isOfflineSupport())
      return (await this.poolDrainOptionsService.GetPoolDrainOptions())?.data;
    return (await this.getPoolOptions()) ?? (await this.setPoolOptions());
  }

  private async setPoolOptions() {
    const data = (await this.poolDrainOptionsService.GetPoolDrainOptions())?.data;
    const encryptData = DataService.encryptData(data);
    await (await this.$db).put("PoolDrainOptions", encryptData, 0);
    return data;
  }

  private async getPoolOptions() {
    const encryptedData = await (await this.$db).get("PoolDrainOptions", 0);
    if (!encryptedData) return null;
    return DataService.decryptData<IPoolDrainOptions[]>(encryptedData);
  }
}
